import { AssetType, Named, WithArdoqPersistent } from './apiTypes';
import { ArdoqId, Email, Entity, MetaData, Versioned } from './coreTypes';
import { QueryBuilderSubquery } from './advancedSearchApiTypes';

export type BroadcastValidation = {
  errors?: Partial<Record<BroadcastError, string>>;
  warnings?: Partial<Record<BroadcastWarning, string>>;
};

export type BroadcastError =
  | 'AUDIENCE_INCOMPLETE'
  | 'CONTENT_INCOMPLETE'
  | 'NAME_MISSING'
  | 'REPORT_ERROR'
  | 'SCHEDULING_INCOMPLETE'
  | 'SENDER_INVALID';

type BroadcastWarning =
  | 'AUDIENCE_PEOPLE_WS_NOT_FOUND'
  | 'AUDIENCE_GREMLIN_INCOMPLETE'
  | 'AUDIENCE_GREMLIN_ERROR'
  | 'AUDIENCE_PREDEFINED_INCOMPLETE'
  | 'AUDIENCE_PREDEFINED_NOT_FOUND'
  | 'AUDIENCE_EMAIL_INCOMPLETE'
  | 'AUDIENCE_EMAIL_MALFORMED'
  | 'SENDER_UNICODE'
  | 'SENDER_MALFORMED'
  | 'CONTENT_NOT_FOUND'
  | 'AUDIENCE_NOT_FOUND';

export enum BroadcastStatus {
  RUNNING = 'running',
  PAUSED = 'paused',
  WARNING = 'warning',
  DONE = 'done',
  UNPUBLISHED = 'unpublished',
}

export enum BroadcastFilterConditionType {
  SURVEY_COMPLETE = 'surveyComplete',
  SURVEY_INCOMPLETE = 'surveyIncomplete',
  SURVEY_NOT_UPDATED_IN = 'surveyNotUpdatedIn',
  ADVANCED_SEARCH = 'advancedSearch',
  ADVANCED_SEARCH_BY_REFERENCE = 'advancedSearchByReference',
}

export type SurveyCompleteFilterCondition = {
  conditionType: BroadcastFilterConditionType.SURVEY_COMPLETE;
};

export type SurveyIncompleteFilterCondition = {
  conditionType: BroadcastFilterConditionType.SURVEY_INCOMPLETE;
};

export enum TimeSinceLastUpdate {
  ONE_MONTH = '1M',
  THREE_MONTHS = '3M',
  SIX_MONTHS = '6M',
  TWELVE_MONTHS = '12M',
}

export type SurveyNotUpdatedSinceFilterCondition = {
  conditionType: BroadcastFilterConditionType.SURVEY_NOT_UPDATED_IN;
  timeSinceLastUpdate: TimeSinceLastUpdate | null;
};

export type AdvancedSearchFilterCondition = {
  conditionType: BroadcastFilterConditionType.ADVANCED_SEARCH;
  advancedQuery: QueryBuilderSubquery;
};

export type AdvancedSearchFilterConditionByReference = {
  conditionType: BroadcastFilterConditionType.ADVANCED_SEARCH_BY_REFERENCE;
  advancedQuery: QueryBuilderSubquery;
  outgoingReferencesSelected: boolean;
  incomingReferencesSelected: boolean;
};

export type BroadcastFilterCondition =
  | SurveyCompleteFilterCondition
  | SurveyIncompleteFilterCondition
  | SurveyNotUpdatedSinceFilterCondition
  | AdvancedSearchFilterCondition
  | AdvancedSearchFilterConditionByReference;

export type BroadcastSurveyContent = {
  contentType: BroadcastContentType.SURVEY;
  contentId: ArdoqId | null;
  filterConditions: BroadcastFilterCondition[];
};

export enum BroadcastContentType {
  MESSAGE = 'message',
  SURVEY = 'survey',
  REPORT = 'report',
}

export type BroadcastMessageContent = {
  contentType: BroadcastContentType.MESSAGE;
  workspaceId: ArdoqId | null;
  componentType: string | null;
  filterConditions: BroadcastFilterCondition[];
};

export enum ReportRuleOperator {
  GREATER = 'greater',
  LESS = 'less',
  EQUAL = 'equal',
  NOT_EQUAL = 'not_equal',
}

// To be supported in reports and dashboards
// NOTE: repeated instead of extending report aggregages to preserve order
export enum BroadcastAggregate {
  SUM = 'sum',
  COUNT = 'count-all',
  COUNT_NOT_SET = 'count-not-set',
  COUNT_SET = 'count-set',
  MAX = 'max',
  MIN = 'min',
  AVERAGE = 'avg',
  AVERAGE_SET = 'avg-set',
  MEDIAN = 'median',
}

export type BroadcastReportContent = {
  contentType: BroadcastContentType.REPORT;
  contentId: ArdoqId | null;
  columnName: string | null;
  aggregate: BroadcastAggregate | null;
  threshold: number | null;
  predicate: ReportRuleOperator | null;
};

export type BroadcastContent =
  | BroadcastSurveyContent
  | BroadcastMessageContent
  | BroadcastReportContent;

export type PredefinedAudience = {
  readonly audienceType: BroadcastAudienceType.PREDEFINED;
  readonly options: PredefinedQueryOption[];
};

export type PredefinedQueryOption = {
  optionType: PredefinedQueryOptionType;
  label: string;
  value: string;
};

enum PredefinedQueryOptionType {
  ATTRIBUTE = 'attribute',
  REFERENCE = 'reference',
}

export type EmailAudience = {
  readonly audienceType: BroadcastAudienceType.EMAIL;
  readonly emails: Email[];
};

export type GremlinAudience = {
  readonly audienceType: BroadcastAudienceType.GREMLIN;
  readonly query: string;
};

export enum BroadcastAudienceType {
  PREDEFINED = 'predefined',
  EMAIL = 'email',
  GREMLIN = 'gremlin',
  WORKSPACE = 'workspace',
}

export type WorkspaceAudienceData = {
  readonly workspaceId: ArdoqId;
  readonly limitedToPersonIds: ArdoqId[];
};

export type WorkspaceAudience = WorkspaceAudienceData & {
  readonly audienceType: BroadcastAudienceType.WORKSPACE;
};

export type BroadcastAudience =
  | PredefinedAudience
  | EmailAudience
  | GremlinAudience
  | WorkspaceAudience;

export enum AudienceStartingPage {
  PLAIN = 'plain',
  DISCOVER_COMPONENT = 'discover-component',
  DISCOVER_SURVEY = 'discover-survey',
  SURVEY_OVERVIEW = 'survey-overview',
  SURVEY_COMPONENT = 'survey-component',
  REPORT_ARDOQ = 'report-ardoq',
  REPORT_DISCOVER = 'report-discover',
  MY_TASKS = 'discover-my-tasks',
}

type BaseSchedule = {
  startDate: string | null;
  reminderInDays: number | null;
  sendOnWeekends?: boolean;
};

export enum IntervalType {
  HOUR = 'hour',
  DAY = 'day',
  WEEK = 'week',
  MONTH = 'month',
  YEAR = 'year',
}

export type BroadcastInterval = {
  intervalValue: number;
  intervalType: IntervalType;
};

export type RepeatingSchedule = BaseSchedule & {
  repeating: true;
  interval: BroadcastInterval;
  endDate?: string | null;
};

type OneOffSchedule = BaseSchedule & {
  repeating: false;
};

export type BroadcastSchedule = OneOffSchedule | RepeatingSchedule;

export type BroadcastMessage = {
  subject: string;
  sender: string;
  body: string;
  companyLogo: boolean | null;
  hideLogo?: boolean | null;
  replyToArdoq: boolean;
};

export type APIBroadcastAttributes = Entity &
  MetaData &
  Named &
  Versioned &
  WithArdoqPersistent & {
    ardoq: { 'entity-type': AssetType.BROADCAST };
    status: BroadcastStatus;
    nextRuntime: string;
    previousRuntime?: string;
    processable: boolean;
    name: string;
    content: BroadcastContent;
    audiences: BroadcastAudience[];
    audienceStartingPage: AudienceStartingPage;
    audienceStartingPageViewpoint?: ArdoqId | null;
    message: BroadcastMessage;
    scheduling: BroadcastSchedule;
  } & BroadcastValidation;

type VersionedId = {
  readonly _id: ArdoqId;
  readonly _version: number;
};

export enum BroadcastScope {
  SOME = 'some',
  ALL = 'all',
}

export interface BroadcastComponent {
  readonly _id: ArdoqId;
  readonly name: string;
}

export type BroadcastPerson = {
  readonly _id: ArdoqId | null;
  readonly name: string | null;
  readonly email: Email | null;
};

export type LightAudiencePreviewRow = {
  readonly componentIds: ArdoqId[];
  readonly person: BroadcastPerson;
  readonly sources: BroadcastAudienceType[];
  readonly scope: BroadcastScope;
};

export type InstanceProgress = Record<ArdoqId, boolean>;

export type APIBroadcastInstanceProgressResponse = {
  progress: InstanceProgress | null;
  isUserInAudience: boolean;
};

export type InstanceComponent = {
  readonly _id: ArdoqId;
  readonly name: string;
};

export type APIAudiencePreviewResponse = {
  audience: LightAudiencePreviewRow[];
  components: BroadcastComponent[];
};

export type AudiencePreviewRow = Omit<
  LightAudiencePreviewRow,
  'componentIds'
> & {
  readonly components: BroadcastComponent[];
};

export type AudiencePreview = AudiencePreviewRow[];

export type BroadcastInstance = {
  readonly _id: ArdoqId;
  readonly broadcast: VersionedId;
  readonly executedDate: string;
  readonly reminderDate: string | null;
  readonly audience: AudiencePreview;
  readonly components?: InstanceComponent[];
  readonly progress: InstanceProgress | null;
};

export type BroadcastPreview = {
  body: string;
  from: string;
  'reply-to': string;
  subject: string;
  to: string;
};

export type ComponentPreviewResponse = {
  readonly components: BroadcastComponent[];
  readonly total: number;
};
